/**
 * Which layer owns the bottom edge of a phone screen.
 *
 * Only ever one at a time. A discovery bar under a resort's "check dates"
 * button would put two competing bottom bars on a 360px screen, so the page
 * family decides, in one place, and every future booking bar asks this same
 * function rather than inventing its own rule.
 *
 * Phase 1 implements "discovery" only; "booking" and "form" describe what the
 * later phases take over, and today simply mean the discovery bar stays away.
 */
export type BottomLayer = "discovery" | "booking" | "form";

/** Pages that run their own submit or booking action along the bottom. */
const FORM_PREFIXES = [
  // Not /cart: it is one of the bar's own four destinations, and its summary
  // is sticky at the top of the page, so nothing competes for the bottom
  // edge. The bar vanishing on arrival made My Trip look broken.
  "/checkout",
  "/plan",
  "/book",
  "/account",
  "/bookings",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/search",
  "/admin",
];

/** A product page: its price and booking action own the bottom edge. */
function isProductDetail(pathname: string): boolean {
  // /stays/<slug> and /stays/<slug>/<room>, /tours/<slug> — but not the
  // directories /stays and /tours, which are discovery.
  return /^\/(stays|tours)\/[^/]+/.test(pathname);
}

export function bottomLayerFor(pathname: string): BottomLayer {
  const path = pathname.split(/[?#]/)[0] || "/";
  if (FORM_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) return "form";
  if (isProductDetail(path)) return "booking";
  return "discovery";
}
